/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip';
import { getErrorCategoryLabel } from '@utils/types/errors/error-categories';
import { useThemeContext } from '@utils/hooks/use-theme-context';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { formatDuration } from '@utils/date';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { useGetTranscriptSessionBo } from '@utils/api/back-office/transcript-sessions/use-get-transcript-sessions-bo';
import type { TranscriptSessionsBo } from '@utils/types/back-office/transcript-sessions';

type Row = {
  id: number;
  session_id: string;
  organization_id: number;
  organization_name: string;
  status: string;
  duration: string;
  language: string;
  started: string;
  finished: string;
  error?: string | null;
  error_category?: string | null;
};

const formatRelativeTime = (
  value: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return t('just-now');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} ${t('min-ago')}`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} ${t('h-ago')}`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} ${t('d-ago')}`;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const mapDocumentType = (type: number): string => {
  switch (type) {
    case 1:
      return 'Transcript';
    case 2:
      return 'Captions';
    case 3:
      return 'Subtitle';
    default:
      return `Type ${type}`;
  }
};

export default function TRAFFICMONITORING() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  const [days, setDays] = useState<number>(7);

  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    null
  );

  const { data, isLoading, isFetching, refetch, isError } =
    useGetTranscriptSessionBo(days);

  const sessions = data?.result ?? [];

  const selectedSession: TranscriptSessionsBo | null = useMemo(
    () => sessions.find(s => s.id === selectedSessionId) ?? null,
    [sessions, selectedSessionId]
  );

  useEffect(() => {
    const id = setInterval(() => refetch(), 60_000);
    return () => clearInterval(id);
  }, [refetch, days]);

  /* ------------------------- Rows ------------------------- */
  const rows = useMemo<Row[]>(() => {
    return sessions.map(item => {
      const durationSeconds = Number(item.duration ?? 0);

      return {
        id: item.id,
        session_id: item.session_id,
        organization_id: item.organization_id,
        organization_name: item.organization_name,
        status: item.status,
        duration: formatDuration(durationSeconds),
        language: item.language,
        started: formatRelativeTime(item.date_session_start, t),
        finished: item.date_finished
          ? formatRelativeTime(item.date_finished, t)
          : t('in-progress'),
        error: item.error,
        error_category: item.error_category
      };
    });
  }, [sessions, t]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'organization',
        header: t('organization-name'),
        field: 'organization_name',
        minWidth: 200,
        enableSorting: true,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <button
              type="button"
              onClick={() =>
                navigate(`/back-office/organizations/${r.organization_id}`)
              }
              className="hover:underline"
              style={{ color: getColour('activeText') }}
            >
              {r.organization_name}
            </button>
          );
        }
      },

      {
        header: t('session-id'),
        field: 'session_id',
        minWidth: 220,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <button
              type="button"
              onClick={() =>
                navigate(`/back-office/transcript-sessions/${r.session_id}`)
              }
              className="font-mono text-xs hover:underline"
              style={{ color: getColour('activeText') }}
            >
              {r.session_id}
            </button>
          );
        }
      },

      {
        id: 'status',
        header: t('status'),
        field: 'status',
        maxWidth: 160,
        enableSorting: true,
        cell: ({ getValue, row }) => {
          const value = String(getValue() ?? '');
          const isError =
            value.toLowerCase() === 'error' || value.toLowerCase() === 'failed';
          const errorMessage = row.original.error;
          const errorCategory = row.original.error_category;

          const badge = <StatusBadge status={value} label={value} />;

          if (isError && errorMessage) {
            return (
              <Tooltip
                label={
                  <div className="max-w-xs text-left">
                    {errorCategory && (
                      <span
                        className="block text-xs font-semibold uppercase mb-1"
                        style={{ color: getColour('activeText') }}
                      >
                        {getErrorCategoryLabel(errorCategory)}
                      </span>
                    )}
                    <span className="text-xs">{errorMessage}</span>
                  </div>
                }
              >
                {badge}
              </Tooltip>
            );
          }

          return badge;
        }
      },

      {
        id: 'duration',
        header: t('duration'),
        field: 'duration',
        maxWidth: 120,
        enableSorting: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },

      {
        header: t('language'),
        field: 'language',
        maxWidth: 120,
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },

      {
        id: 'date_session_start',
        header: t('started'),
        field: 'started',
        maxWidth: 140,
        enableSorting: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },

      {
        header: t('finished'),
        field: 'finished',
        maxWidth: 160,
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ];
  }, [t, navigate, getColour]);

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'details',
        label: t('details'),
        icon: <ChevronRight className="h-4 w-4" />,
        sticky: true,
        isActive: row => selectedSessionId === row.id,
        run: row =>
          setSelectedSessionId(prev => (prev === row.id ? null : row.id))
      }
    ],
    [t, selectedSessionId]
  );

  const ranges = [
    { value: 1, label: '24h' },
    { value: 7, label: '7 days' },
    { value: 30, label: '30 days' }
  ];

  const handleExportCsv = () => {
    if (!rows.length) return;

    const headers = [
      'id',
      'session_id',
      'organization_id',
      'organization_name',
      'status',
      'duration',
      'language',
      'started',
      'finished'
    ];

    const csv = [
      headers.join(','),
      ...rows.map(r =>
        [
          r.id,
          `"${r.session_id}"`,
          r.organization_id,
          `"${r.organization_name.replace(/"/g, '""')}"`,
          r.status,
          r.duration,
          r.language,
          r.started,
          r.finished
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `traffic-sessions-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col px-4 py-2">
      <PageHeader
        title={t('traffic-monitoring')}
        caption={t('traffic-monitoring-with-details')}
      >
        <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="inline-flex items-center gap-1 border-r border-hairline px-2 py-1 text-xs">
            <span
              className="mr-1 text-xs uppercase tracking-wide"
              style={{ color: getColour('inactiveText') }}
            >
              {t('range')}:
            </span>
            {ranges.map(range => {
              const isActive = days === range.value;
              return (
                <button
                  key={range.value}
                  type="button"
                  onClick={() => setDays(range.value)}
                  aria-pressed={isActive}
                  className="rounded-full px-2.5 py-0.5 text-xs transition border border-hairline"
                  style={{
                    backgroundColor: isActive
                      ? getColour('thirdLayer')
                      : 'transparent',
                    color: isActive
                      ? getColour('normalText')
                      : getColour('inactiveText'),
                    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  {range.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleExportCsv}
              variant="secondary"
              label={t('export-csv')}
            />
            <Button onClick={() => refetch()} label={t('refresh')} />
          </div>
        </div>
      </PageHeader>

      {/* Error banner */}
      {isError && (
        <div className="flex items-center gap-2 rounded-2xl bg-red-500/10 border border-red-500/25 px-4 py-3 mb-4 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{t('failed-to-load-data')}</span>
        </div>
      )}

      {/* Details panel */}
      {selectedSession && (
        <div
          className="mb-4 rounded-2xl border border-hairline p-4 text-sm"
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <div
                className="text-xs uppercase tracking-wide"
                style={{ color: getColour('inactiveText') }}
              >
                {t('session-details')}
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {selectedSession.organization_name}
              </div>
              <div
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {selectedSession.session_id}
              </div>
            </div>
            <StatusBadge
              status={selectedSession.status}
              label={selectedSession.status}
            />
          </div>

          {/* Timeline */}
          <div className="mb-3">
            <div
              className="mb-1 text-xs font-semibold uppercase"
              style={{ color: getColour('inactiveText') }}
            >
              {t('timeline')}
            </div>
            <div className="flex flex-col gap-1 text-xs md:flex-row md:items-start md:gap-4">
              <div>
                <div
                  className="font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('date-upload')}
                </div>
                <div style={{ color: getColour('inactiveText') }}>
                  {formatDateTime(selectedSession.date_upload)}
                </div>
              </div>
              <div>
                <div
                  className="font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('date-transcoded')}
                </div>
                <div style={{ color: getColour('inactiveText') }}>
                  {formatDateTime(selectedSession.date_transcoded)}
                </div>
              </div>
              <div>
                <div
                  className="font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('date-transcribed')}
                </div>
                <div style={{ color: getColour('inactiveText') }}>
                  {formatDateTime(selectedSession.date_transcribed)}
                </div>
              </div>
              <div>
                <div
                  className="font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('date-finished')}
                </div>
                <div style={{ color: getColour('inactiveText') }}>
                  {formatDateTime(selectedSession.date_finished)}
                </div>
              </div>
            </div>
          </div>

          {/* Processing details */}
          <div className="mb-3 grid gap-2 text-xs sm:grid-cols-4">
            <div>
              <div
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('session-mode')}
              </div>
              <div style={{ color: getColour('inactiveText') }}>
                {selectedSession.session_mode || '—'}
              </div>
            </div>
            <div>
              <div
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('has-video')}
              </div>
              <div style={{ color: getColour('inactiveText') }}>
                {selectedSession.has_video ? t('yes') : t('no')}
              </div>
            </div>
            <div>
              <div
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('diarization')}
              </div>
              <div style={{ color: getColour('inactiveText') }}>
                {selectedSession.diarization ? t('yes') : t('no')}
              </div>
            </div>
            <div>
              <div
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('keep-media')}
              </div>
              <div style={{ color: getColour('inactiveText') }}>
                {selectedSession.keep_media ? t('yes') : t('no')}
              </div>
            </div>
          </div>

          {/* Error details */}
          {selectedSession.error && (
            <div className="mb-3 rounded-2xl border bg-red-500/10 border-red-500/25 dark:bg-red-500/15 dark:border-red-500/35 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase text-red-700 dark:text-red-400">
                  {t('error-message')}
                </div>
                {selectedSession.error_category && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold uppercase bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400">
                    {getErrorCategoryLabel(selectedSession.error_category)}
                  </span>
                )}
              </div>
              <div className="text-xs text-red-700 dark:text-red-400">
                {selectedSession.error}
              </div>
            </div>
          )}

          {/* Documents */}
          <div>
            <div
              className="mb-1 text-xs font-semibold uppercase"
              style={{ color: getColour('inactiveText') }}
            >
              {t('documents')}
            </div>
            {selectedSession.documents &&
            selectedSession.documents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-xs">
                  <thead>
                    <tr
                      className="border-b border-hairline"
                      style={{ backgroundColor: getColour('thirdLayer') }}
                    >
                      <th
                        className="px-2 py-1 text-left font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('filename')}
                      </th>
                      <th
                        className="px-2 py-1 text-left font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('type')}
                      </th>
                      <th
                        className="px-2 py-1 text-left font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('created')}
                      </th>
                      <th
                        className="px-2 py-1 text-left font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('saved')}
                      </th>
                      <th
                        className="px-2 py-1 text-left font-medium"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('finished')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSession.documents.map(doc => (
                      <tr
                        key={doc.id}
                        className="border-b border-hairline last:border-0"
                        style={{ color: getColour('normalText') }}
                      >
                        <td className="px-2 py-1">{doc.filename}</td>
                        <td className="px-2 py-1">
                          {mapDocumentType(doc.document_type)}
                        </td>
                        <td className="px-2 py-1">
                          {formatDateTime(doc.created)}
                        </td>
                        <td className="px-2 py-1">
                          {doc.saved ? t('yes') : t('no')}
                        </td>
                        <td className="px-2 py-1">
                          {doc.finished ? t('yes') : t('no')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('no-documents')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop/tablet table */}
      <div className="hidden w-full p-2 md:flex md:flex-col flex-1 min-h-0 md:p-0">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection={false}
          enableSorting
          manualSorting
          enablePagination
          manualPagination
          totalItems={data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 100]}
          enableFiltering={false}
          manualFiltering={false}
          showColumnFilters={false}
          fill
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !data}
          getRowId={row => String(row.id)}
          actions={actions}
        />
      </div>

      {/* Mobile card view */}
      <div className="block w-full p-2 md:hidden">
        {sessions.length === 0 && !isLoading && !isFetching ? (
          <div
            className="py-8 flex flex-col items-center justify-center text-center text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            <p>{t('no-traffic-sessions-found')}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sessions.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() =>
                  setSelectedSessionId(
                    selectedSessionId === item.id ? null : item.id
                  )
                }
                className="w-full rounded-2xl border border-hairline p-3 text-left text-xs"
                style={{ backgroundColor: getColour('secondLayer') }}
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div
                    className="font-medium"
                    style={{ color: getColour('normalText') }}
                  >
                    {item.organization_name || '—'}
                  </div>
                  <StatusBadge status={item.status} label={item.status} />
                </div>
                <div
                  className="mb-1 font-mono text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {item.session_id}
                </div>
                <div
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  <span>
                    {t('duration')}:{' '}
                    {formatDuration(Number(item.duration ?? 0))}
                  </span>
                  <span>
                    {t('language')}: {item.language}
                  </span>
                  <span>
                    {t('started')}:{' '}
                    {formatRelativeTime(item.date_session_start, t)}
                  </span>
                  <span>
                    {t('finished')}:{' '}
                    {item.date_finished
                      ? formatRelativeTime(item.date_finished, t)
                      : t('in-progress')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
